import { isNil } from '@lucidlink/tscore';
import { InvalidArgumentError } from '#common/types';
import { createTextEncoder } from '#common/util';
import { fetchHttpFileMetadata, HttpMetadataNetworkError, sanitizeUrl } from '#object-store/common';
import { PresignedUrlHelper } from './presigned-url-helper';
import { DataStoreInvalidTypeError, DataStoreKind, DataStoreNotFoundError, HttpFileUnreachableError, HttpFileValidationError, } from './types';
export class ExternalFilesHelper {
    dataStores;
    fsModule;
    commonConfigKeyHelper;
    encoder = createTextEncoder();
    presignedUrlHelper;
    constructor(tupleWasm, dataStores, adminCrypto, fsModule, commonConfigKeyHelper) {
        this.dataStores = dataStores;
        this.fsModule = fsModule;
        this.commonConfigKeyHelper = commonConfigKeyHelper;
        this.presignedUrlHelper = new PresignedUrlHelper(tupleWasm, adminCrypto);
    }
    async createSingleObjectFile(filePath, file) {
        this.validateMetadata(file.metadata);
        const info = await this.getDataStore(file.dataStoreName);
        if (info.kind !== DataStoreKind.S3) {
            throw new DataStoreInvalidTypeError(`Data store '${file.dataStoreName}' must be an S3 data store, got: ${info.kind}`);
        }
        const { url, size, checksum } = await this.presignedUrlHelper.generatePresignedUrl(file.objectId, info, file.metadata);
        const encryptedObjectId = await this.commonConfigKeyHelper.encryptCommon(this.encoder.encode(file.objectId));
        const encryptedUrl = await this.commonConfigKeyHelper.encryptCommon(this.encoder.encode(url), encryptedObjectId.iv);
        return await this.fsModule.createSingleObjectFile({
            filePath,
            encryptedUrl: encryptedUrl.value,
            encryptedObjectId: encryptedObjectId.value,
            iv: encryptedObjectId.iv,
            checksum,
            size,
            dataStoreKeyId: info.keyId,
        });
    }
    async createHttpFile(filePath, file) {
        this.validateNonNegativeSize(file.size);
        const size = file.size ?? (await this.fetchHttpFileSize(file.url));
        return await this.fsModule.createHttpFile(filePath, file.url, size, file.dataStoreName);
    }
    async updateHttpFileUrl(filePath, url) {
        const size = await this.fetchHttpFileSize(url);
        return await this.fsModule.updateHttpFileUrl(filePath, url, size);
    }
    setExpiresInSeconds(value) {
        this.presignedUrlHelper.setExpiresInSeconds(value);
    }
    validateMetadata(metadata) {
        if (isNil(metadata)) {
            return;
        }
        this.validateNonNegativeSize(metadata.size);
        if (metadata.checksum.length === 0) {
            throw new InvalidArgumentError('checksum must be a non-empty string');
        }
    }
    validateNonNegativeSize(size) {
        if (!isNil(size) && size < 0n) {
            throw new InvalidArgumentError('size must be a non-negative integer');
        }
    }
    async fetchHttpFileSize(url) {
        try {
            const { size } = await fetchHttpFileMetadata(url, {
                onUnexpectedHttpStatus: (response) => {
                    throw new HttpFileValidationError(`HTTP file URL is not accessible: ${response.status} ${response.statusText}`, `URL: ${sanitizeUrl(url)}`);
                },
            });
            return size;
        }
        catch (error) {
            if (error instanceof HttpFileValidationError) {
                throw error;
            }
            const message = error instanceof Error ? error.message : 'Unknown error';
            const errorMessage = `Failed to validate HTTP file URL: ${message}`;
            const devDetails = `URL: ${sanitizeUrl(url)}`;
            if (error instanceof HttpMetadataNetworkError) {
                throw new HttpFileUnreachableError(errorMessage, devDetails);
            }
            throw new HttpFileValidationError(errorMessage, devDetails);
        }
    }
    async getDataStore(dataStoreName) {
        const info = await this.dataStores.get(dataStoreName);
        if (isNil(info)) {
            throw new DataStoreNotFoundError(`Data store with name ${dataStoreName} does not exist`);
        }
        return info;
    }
}
